import { createContext, useContext, ReactNode, useState, useEffect } from 'react';
import { errorToast, successToast } from '../../../CommonComponent/toast';
import useFetchWithAuth from '../../../CommonHooks/useFetchWithAuth';
import { isEqual } from 'lodash';
import { useMainContainerProvider } from '../../../mainContainerProvider';
import { AccessControlContextType, AccessControlPermission } from '../types';

const AccessControlContext = createContext<AccessControlContextType | undefined>(undefined);

type AccessControlProviderProps = {
  children: ReactNode;
};

const AccessControlProvider = ({ children }: AccessControlProviderProps) => {
  const [permissions, setPermissions] = useState<AccessControlPermission[]>([]);
  const [updatedPermission, setUpdatedPermission] = useState<AccessControlPermission[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const { fetchWithAuth } = useFetchWithAuth();
  const { userType, uid, instituteId } = useMainContainerProvider();

  const getEmployeeAccessList = async () => {
    setIsLoading(true);
    const { error, data } = await fetchWithAuth(`access-control/?userType=${userType}&uid=${uid}&instituteId=${instituteId}`);

    if (error) {
      errorToast(error);
      setIsLoading(false);
      return;
    }
    setPermissions(data);
    setUpdatedPermission(data);
    setIsLoading(false);
  };

  const updateEmployeeAccessList = async () => {
    const accessRecords = {
      uid: uid,
      userType: userType,
      instituteId: instituteId,
      employeeAccess: updatedPermission,
    };
    setIsLoading(true);
    const payload = {
      method: 'POST',
      body: JSON.stringify(accessRecords),
    };
    const { error, data } = await fetchWithAuth(`access-control/`, payload);

    if (error) {
      errorToast(error);
      setIsLoading(false);
      return;
    }
    setPermissions(data);
    setUpdatedPermission(data);
    successToast('Access Updated successfully');
    setIsLoading(false);
  };

  useEffect(() => {
    if (permissions.length === 0) {
      getEmployeeAccessList();
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!isEqual(updatedPermission, permissions)) {
      setIsDirty(true);
    } else {
      setIsDirty(false);
    }
    // eslint-disable-next-line
  }, [updatedPermission]);

  return (
    <AccessControlContext.Provider
      value={{
        permissions: updatedPermission,
        setPermissions: setUpdatedPermission,
        isLoading,
        isDirty,
        updateEmployeeAccessList,
      }}
    >
      {children}
    </AccessControlContext.Provider>
  );
};

export default AccessControlProvider;

export const useAccessControl = () => {
  const context = useContext(AccessControlContext);
  if (!context) {
    throw new Error('useAccessControl must be used within an AccessControlProvider');
  }
  return context;
};
